/**
 * How many turns in a row a chat member may take on its own in one thread (#283, #287): each woken by the ending of
 * work the thread started, with nobody writing. The enterprise chooses it on its Enterprise page, from 0 (off) to
 * `maxLimit`; one that never chose gets `defaultLimit`. The API refuses against these, the chat sweep reads the limit
 * in force through `chatUnpromptedTurnLimitInForce`, and the portal offers exactly these choices, so none of them
 * holds a figure of its own and they cannot drift.
 */
export type ChatUnpromptedTurnLimits = {
    /** The limit for an enterprise that never set one. */
    defaultLimit: number
    /** The highest limit an enterprise may set, inclusive: it bounds what turns on its own may spend. */
    maxLimit: number
}

/**
 * The one published contract (#287): 5 by default, at most 20. Twenty is four times the default and the cap on a
 * member's step list (`CHAT_STEP_LIMITS.maxSteps`), so a member can carry each step it lined up on its own.
 */
export const CHAT_UNPROMPTED_TURN_LIMITS: ChatUnpromptedTurnLimits = { defaultLimit: 5, maxLimit: 20 }

/** Whether a value arriving over the wire is a limit the OS saves: a whole number from 0 (off) to the highest. */
export function isChatUnpromptedTurnLimit(value: unknown): value is number {
    return (
        typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= 0 &&
        value <= CHAT_UNPROMPTED_TURN_LIMITS.maxLimit
    )
}

/**
 * The limit in force for what an enterprise has stored: unset (null), or anything that is not a number of turns,
 * reads as the default; 0 is off and stays 0; a fraction counts its whole turns; and nothing above the highest
 * applies, so the limit shown and the limit the sweep holds a member to are always one value.
 */
export function chatUnpromptedTurnLimitInForce(stored: number | null | undefined): number {
    if (typeof stored !== 'number' || !Number.isFinite(stored) || stored < 0) {
        return CHAT_UNPROMPTED_TURN_LIMITS.defaultLimit
    }
    return Math.min(Math.floor(stored), CHAT_UNPROMPTED_TURN_LIMITS.maxLimit)
}

/** Every limit an enterprise may choose, in order: 0 (off), then 1 to the highest. */
export function chatUnpromptedTurnLimitChoices(): number[] {
    return Array.from({ length: CHAT_UNPROMPTED_TURN_LIMITS.maxLimit + 1 }, (_unused, limit) => limit)
}

/** A limit as a person reads it: `Off` for 0, then `1 turn`, `5 turns`. */
export function chatUnpromptedTurnLimitLabel(limit: number): string {
    if (limit === 0) {
        return 'Off'
    }
    return `${limit} ${limit === 1 ? 'turn' : 'turns'}`
}
